import React, { useState } from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { BeheerLayout } from '../../components/beheer/BeheerLayout';
import { BeheerSection } from '../../components/beheer/BeheerSection';
import { AgentRoster } from '../../components/beheer/agents/AgentRoster';
import { PERMISSION_META, PermissionMatrix } from '../../components/beheer/agents/PermissionMatrix';
import { capabilities, defaultPermissions } from '../../data/beheer/permissions';
import { actionLevels } from '../../data/levels';
import { getAgent } from '../../utils/agents';
import { db } from '../../utils/db';
import type { AgentId } from '../../types/anna';
import type { CapabilityId, Permission, PermissionMatrix as Matrix } from '../../types/settings';
export function Agents() {
  const [matrix, setMatrix] = useState<Matrix>(() => structuredClone(defaultPermissions));
  const [paused, setPaused] = useState<Record<string, boolean>>({});
  const changed = JSON.stringify(matrix) !== JSON.stringify(defaultPermissions);
  const change = (agent: AgentId, cap: CapabilityId, next: Permission) => {
    setMatrix(m => ({
      ...m,
      [agent]: {
        ...m[agent],
        [cap]: next
      }
    }));
    const c = capabilities.find(x => x.id === cap);
    db.log({
      agent: 'systeem',
      summary: `Recht ${getAgent(agent).name} · ${c?.label.toLowerCase()} → ${PERMISSION_META[next].short.toLowerCase()}`,
      level: 'naOk',
      where: 'lokaal',
      outcome: 'goedgekeurd'
    });
  };
  const pause = (agent: AgentId, value: boolean) => {
    setPaused(p => ({
      ...p,
      [agent]: value
    }));
    db.log({
      agent,
      summary: value ? `${getAgent(agent).name}-agent gepauzeerd via beheer` : `${getAgent(agent).name}-agent weer actief`,
      level: 'zelf',
      where: 'lokaal',
      outcome: value ? 'gestopt' : 'uitgevoerd'
    });
  };
  const active = Object.values(paused).filter(Boolean).length;
  return <BeheerLayout title="Agents" intro="Wie werkt er voor je, waaraan, en wat mag elke agent zelf. Elke wijziging komt in het logboek." aside={<p className="text-[14px] text-ink-soft" data-id="element-1808" data-mp="32q2qbemqglry">{8 - active} van 8 actief</p>} data-id="element-1807" data-mp="3sft163z0fp4n">
      <BeheerSection id="roster" title="Nu" description="Status, model en cloudkosten per agent." className="mt-10" data-id="element-1809" data-mp="2xunfkvk0v5zr">
        <AgentRoster paused={paused} onPause={pause} data-id="element-1810" data-mp="3ck33m3s64xbr" />
      </BeheerSection>

      <BeheerSection id="rechten" title="Rechten" description="Tik op een cel om het niveau te wijzigen. Sloten, betalen en verwijderen staan vast op Nooit." className="mt-16" action={changed && <button type="button" onClick={() => setMatrix(structuredClone(defaultPermissions))} className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1812" data-mp="3vgktsgrdcp5w">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1813" data-mp="1w51smocb4l6s" /> Standaard herstellen
            </button>} data-id="element-1811" data-mp="37pp4preas82c">
        <PermissionMatrix matrix={matrix} onChange={change} data-id="element-1814" data-mp="bnsk4z3x1fma" />
        <dl className="mt-6 grid gap-x-8 gap-y-3 text-[13px] sm:grid-cols-2 xl:grid-cols-4" data-id="element-1815" data-mp="gjhe8mx624b7">
          {actionLevels.map(l => <div key={l.id} data-id="element-1816" data-mp="1ts96glydg5n">
              <dt className="font-medium text-ink-soft" data-id="element-1817" data-mp="6uprquavkc7o">{l.label}</dt>
              <dd className="mt-0.5 leading-relaxed text-ink-muted" data-id="element-1818" data-mp="6swh73x557ue">{l.rule}</dd>
            </div>)}
        </dl>
      </BeheerSection>
    </BeheerLayout>;
}